import { useState, useEffect, useCallback } from 'react'
import { useFormData, useFormActions, useFormStore, useIdentityDocumentsStatus } from '@hooks/useFormStore'
import { submissionsApi, analyticsApi, identityDocumentsApi } from '@services/api'
import { Button } from '@components/UI/Button'
import { clsx } from 'clsx'
import { isIdentityDocumentRequired, isIdentityAttachmentRequired } from '@/types'
import type { GeneratedDocument } from '@/types'
import styles from './FormSection.module.css'

export function RiepilogoSection() {
  const formData = useFormData()
  const { prevStep, setStep, setSubmissionId, setGeneratedDocuments, setIdentityDocumentsStatus } = useFormActions()
  const submissionId = useFormStore((state) => state.submissionId)
  const ocrAssisted = useFormStore((state) => state.ocrAssisted)
  const identityDocsStatus = useIdentityDocumentsStatus()
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Estremi documento richiesti per switch, subentro e voltura; allegato
  // fronte/retro solo per subentro e voltura (switch esente dal 21/09/2026).
  const documentRequired = isIdentityDocumentRequired(formData)
  const attachmentRequired = isIdentityAttachmentRequired(formData)
  const hasRequiredDocs = identityDocsStatus?.has_required_documents ?? false
  const canSubmit = !attachmentRequired || hasRequiredDocs
  
  // Refresh identity documents status on mount
  const refreshIdentityDocsStatus = useCallback(async () => {
    if (documentRequired) {
      try {
        const status = await identityDocumentsApi.getStatus()
        setIdentityDocumentsStatus(status)
      } catch (error) {
        console.error('Error fetching identity documents status:', error)
      }
    }
  }, [documentRequired, setIdentityDocumentsStatus])

  useEffect(() => {
    refreshIdentityDocsStatus()
  }, [refreshIdentityDocsStatus])

  const handleSubmit = async () => {
    // Check if identity document is required but not uploaded
    if (attachmentRequired && !hasRequiredDocs) {
      setError('Documento d\'identità obbligatorio per Subentro/Voltura. Torna alla sezione Documento per caricare fronte e retro.')
      return
    }
    
    setIsSubmitting(true)
    setError(null)

    try {
      let currentSubmissionId = submissionId
      let submitResponse: { documents?: GeneratedDocument[] } | undefined
      
      if (currentSubmissionId) {
        // Aggiorna e invia submission esistente
        await submissionsApi.update(currentSubmissionId, formData)
        submitResponse = await submissionsApi.submit(currentSubmissionId)
      } else {
        // Crea e invia nuova submission
        const { id } = await submissionsApi.create({
          ...formData
        })
        currentSubmissionId = id
        setSubmissionId(id)
        submitResponse = await submissionsApi.submit(id)
      }

      analyticsApi.track('submit', 'form_submitted', {
        data: {
          tipo_fornitura: formData.tipo_fornitura,
          ocr_assisted: ocrAssisted
        }
      })

      // Salva i documenti generati nello store per il processo di firma
      if (submitResponse?.documents && submitResponse.documents.length > 0) {
        setGeneratedDocuments(submitResponse.documents)
      }
      
      // Procedi al processo di firma
      setStep('otp_request')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore durante l\'invio'
      setError(message)
      analyticsApi.trackError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Note: After successful submit, we proceed directly to signing flow (otp_request step)

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <i className="bi bi-check-circle-fill" />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>Riepilogo</h2>
          <p className={styles.sectionDescription}>
            Verifica i dati prima di inviare la richiesta
          </p>
        </div>
      </div>

      {/* Dati Azienda (solo business) */}
      {formData.tipo_cliente === 'business' && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-building" />
            <span>Dati Azienda</span>
            <button type="button" className={styles.editButton} onClick={() => setStep('anagrafica')} disabled={isSubmitting}>
              <i className="bi bi-pencil" /> Modifica
            </button>
          </div>
          <div className={styles.summaryGrid}>
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Ragione Sociale</span>
              <span className={styles.summaryValue}>{formData.ragione_sociale}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Partita IVA</span>
              <span className={styles.summaryValue}>{formData.partita_iva}</span>
            </div>
            {formData.codice_sdi && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Codice SDI</span>
                <span className={styles.summaryValue}>{formData.codice_sdi}</span>
              </div>
            )}
            {formData.pec_azienda && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>PEC Azienda</span>
                <span className={styles.summaryValue}>{formData.pec_azienda}</span>
              </div>
            )}
            {formData.codice_fiscale_azienda && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Codice Fiscale Azienda</span>
                <span className={styles.summaryValue}>{formData.codice_fiscale_azienda}</span>
              </div>
            )}
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Sede Legale</span>
              <span className={styles.summaryValue}>
                {formData.sede_legale_indirizzo}{formData.sede_legale_civico ? ` ${formData.sede_legale_civico}` : ''}, {formData.sede_legale_cap} {formData.sede_legale_citta} ({formData.sede_legale_provincia})
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Rappresentante Legale</span>
              <span className={styles.summaryValue}>
                {formData.rappresentante_legale_nome} {formData.rappresentante_legale_cognome}
              </span>
            </div>
            {formData.rappresentante_legale_cf && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>C.F. Rappresentante</span>
                <span className={styles.summaryValue}>{formData.rappresentante_legale_cf}</span>
              </div>
            )}
            {formData.agevolazione_iva && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Agevolazione IVA</span>
                <span className={styles.summaryValue}>{formData.agevolazione_iva}</span>
              </div>
            )}
            {formData.agevolazione_accise && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Agevolazione Accise</span>
                <span className={styles.summaryValue}>{formData.agevolazione_accise}</span>
              </div>
            )}
            {formData.referente_azienda_nome && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Referente Azienda</span>
                <span className={styles.summaryValue}>{formData.referente_azienda_nome}</span>
              </div>
            )}
            {formData.referente_azienda_telefono && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Tel. Referente</span>
                <span className={styles.summaryValue}>{formData.referente_azienda_telefono}</span>
              </div>
            )}
            {formData.codice_ateco && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Codice ATECO</span>
                <span className={styles.summaryValue}>{formData.codice_ateco}</span>
              </div>
            )}
            {formData.tipo_attivita && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Tipo Attivita</span>
                <span className={styles.summaryValue}>{formData.tipo_attivita.replace(/_/g, ' ')}</span>
              </div>
            )}
            {formData.tensione_fornitura && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Tensione</span>
                <span className={styles.summaryValue}>{formData.tensione_fornitura === 'bt' ? 'Bassa Tensione' : 'Media Tensione'}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dati Anagrafici */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-person" />
          <span>{formData.tipo_cliente === 'business' ? 'Rappresentante Legale' : 'Dati Anagrafici'}</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('anagrafica')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Nome e Cognome</span>
            <span className={styles.summaryValue}>
              {formData.nome} {formData.cognome}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Codice Fiscale</span>
            <span className={styles.summaryValue}>{formData.codice_fiscale}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Telefono</span>
            <span className={styles.summaryValue}>{formData.telefono}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Email</span>
            <span className={styles.summaryValue}>{formData.email}</span>
          </div>
          {(formData.data_nascita || formData.luogo_nascita) && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Nascita</span>
              <span className={styles.summaryValue}>
                {[formData.data_nascita, formData.luogo_nascita && `${formData.luogo_nascita}${formData.provincia_nascita ? ` (${formData.provincia_nascita})` : ''}`].filter(Boolean).join(' - ')}
              </span>
            </div>
          )}
          {formData.pec && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>PEC</span>
              <span className={styles.summaryValue}>{formData.pec}</span>
            </div>
          )}
          <div className={clsx(styles.summaryItem, styles.fullWidth)}>
            <span className={styles.summaryLabel}>
              {formData.tipo_cliente === 'business' ? 'Indirizzo Rappresentante' : 'Indirizzo di Residenza'}
            </span>
            <span className={styles.summaryValue}>
              {formData.indirizzo_residenza}, {formData.cap_residenza} {formData.citta_residenza} ({formData.provincia_residenza})
            </span>
          </div>
          <div className={clsx(styles.summaryItem, styles.fullWidth)}>
            <span className={styles.summaryLabel}>Indirizzo di Fornitura</span>
            <span className={styles.summaryValue}>
              {formData.stesso_indirizzo ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-success-500)', fontSize: '0.875rem' }} />
                  {formData.tipo_cliente === 'business' ? 'Coincide con la sede legale' : 'Coincide con l\'indirizzo di residenza'}
                </span>
              ) : (
                `${formData.indirizzo_fornitura}, ${formData.cap_fornitura} ${formData.citta_fornitura} (${formData.provincia_fornitura})`
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Dati Fornitura */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-lightning-charge" />
          <span>Fornitura</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('fornitura')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Tipo</span>
            <span className={styles.summaryValue}>
              {formData.tipo_fornitura === 'entrambi' ? 'Luce + Gas' : 
               formData.tipo_fornitura === 'luce' ? 'Solo Luce' : 'Solo Gas'}
            </span>
          </div>
          
          {formData.ha_luce && (
            <>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>POD</span>
                <span className={styles.summaryValue}>{formData.pod_luce || '-'}</span>
              </div>
              {formData.potenza_impegnata && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Potenza</span>
                  <span className={styles.summaryValue}>{formData.potenza_impegnata} kW</span>
                </div>
              )}
              {formData.tipo_attivazione_luce && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Tipo Attivazione Luce</span>
                  <span className={styles.summaryValue}>
                    {formData.tipo_attivazione_luce === 'cambio_fornitore_voltura' && 'Cambio Fornitore con Voltura'}
                    {formData.tipo_attivazione_luce === 'cambio_fornitore' && 'Cambio Fornitore'}
                  </span>
                </div>
              )}
              {formData.consumo_annuo_luce != null && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Consumo Annuo Luce</span>
                  <span className={styles.summaryValue}>{formData.consumo_annuo_luce} kWh/anno</span>
                </div>
              )}
              {formData.fornitore_attuale_luce && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Fornitore Attuale Luce</span>
                  <span className={styles.summaryValue}>{formData.fornitore_attuale_luce}</span>
                </div>
              )}
            </>
          )}
          
          {formData.ha_gas && (
            <>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>PDR</span>
                <span className={styles.summaryValue}>{formData.pdr_gas || '-'}</span>
              </div>
              {formData.matricola_contatore && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Matricola</span>
                  <span className={styles.summaryValue}>{formData.matricola_contatore}</span>
                </div>
              )}
              {formData.tipo_attivazione_gas && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Tipo Attivazione Gas</span>
                  <span className={styles.summaryValue}>
                    {formData.tipo_attivazione_gas === 'cambio_fornitore_voltura' && 'Cambio Fornitore con Voltura'}
                    {formData.tipo_attivazione_gas === 'cambio_fornitore' && 'Cambio Fornitore'}
                  </span>
                </div>
              )}
              {formData.consumo_annuo_gas != null && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Consumo Annuo Gas</span>
                  <span className={styles.summaryValue}>{formData.consumo_annuo_gas} Smc/anno</span>
                </div>
              )}
              {formData.fornitore_attuale_gas && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Fornitore Attuale Gas</span>
                  <span className={styles.summaryValue}>{formData.fornitore_attuale_gas}</span>
                </div>
              )}
              {formData.categoria_uso_gas && formData.categoria_uso_gas.length > 0 && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Categoria Uso Gas</span>
                  <span className={styles.summaryValue}>{formData.categoria_uso_gas.join(', ')}</span>
                </div>
              )}
            </>
          )}

          {formData.ha_luce && formData.tipo_attivazione_luce === 'cambio_fornitore_voltura' && formData.vecchio_intestatario_luce && (
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Vecchio Intestatario Luce</span>
              <span className={styles.summaryValue}>
                {[formData.vecchio_intestatario_luce.nome, formData.vecchio_intestatario_luce.cognome, formData.vecchio_intestatario_luce.codice_fiscale].filter(Boolean).join(' - ')}
              </span>
            </div>
          )}
          {formData.ha_gas && formData.tipo_attivazione_gas === 'cambio_fornitore_voltura' && formData.vecchio_intestatario_gas && (
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Vecchio Intestatario Gas</span>
              <span className={styles.summaryValue}>
                {[formData.vecchio_intestatario_gas.nome, formData.vecchio_intestatario_gas.cognome, formData.vecchio_intestatario_gas.codice_fiscale].filter(Boolean).join(' - ')}
              </span>
            </div>
          )}

          {formData.ha_luce && (formData.prodotto_luce || formData.prodotto_luce_id) && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Prodotto Luce</span>
              <span className={styles.summaryValue}>{formData.prodotto_luce || formData.prodotto_luce_id}</span>
            </div>
          )}
          {formData.ha_gas && (formData.prodotto_gas || formData.prodotto_gas_id) && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Prodotto Gas</span>
              <span className={styles.summaryValue}>{formData.prodotto_gas || formData.prodotto_gas_id}</span>
            </div>
          )}
          {formData.company && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Società</span>
              <span className={styles.summaryValue}>
                {formData.company === 'heracomm' ? 'Heracomm' : formData.company === 'estenergy' ? 'EstEnergy' : formData.company}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pagamento */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-credit-card" />
          <span>Pagamento</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('pagamento')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Metodo</span>
            <span className={styles.summaryValue}>
              {formData.metodo_pagamento === 'iban'
                ? 'Addebito su conto'
                : formData.metodo_pagamento === 'pagoPA'
                  ? 'Bollettino PagoPA'
                  : '—'}
            </span>
          </div>
          {formData.metodo_pagamento === 'iban' && formData.iban && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>IBAN</span>
              <span className={styles.summaryValue}>{formData.iban}</span>
            </div>
          )}
          {formData.metodo_pagamento === 'iban' && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>IBAN intestato a me</span>
              <span className={styles.summaryValue}>
                {formData.iban_intestato_a_me === false ? 'No' : 'Sì'}
              </span>
            </div>
          )}
          {formData.metodo_pagamento === 'iban' && formData.iban_intestato_a_me === false && formData.intestatario_iban && (
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Intestatario IBAN</span>
              <span className={styles.summaryValue}>
                {[formData.intestatario_iban.nome, formData.intestatario_iban.cognome, formData.intestatario_iban.codice_fiscale].filter(Boolean).join(' - ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Invio Bolletta */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-envelope" />
          <span>Invio Bolletta</span>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Modalità di invio</span>
            <span className={styles.summaryValue}>
              {formData.modalita_invio_bolletta === 'email' ? 'Email' : formData.modalita_invio_bolletta === 'posta' ? 'Posta' : formData.modalita_invio_bolletta || '-'}
            </span>
          </div>
          {formData.modalita_invio_bolletta === 'email' && formData.invio_email_tipo && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Email per invio</span>
              <span className={styles.summaryValue}>
                {formData.invio_email_tipo === 'altro' ? (formData.invio_email_altro || '-') : 'Email anagrafica'}
              </span>
            </div>
          )}
          {formData.modalita_invio_bolletta === 'posta' && formData.invio_indirizzo_tipo && (
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Indirizzo per invio</span>
              <span className={styles.summaryValue}>
                {formData.invio_indirizzo_tipo === 'residenza' && 'Indirizzo di residenza'}
                {formData.invio_indirizzo_tipo === 'fornitura' && 'Indirizzo di fornitura'}
                {formData.invio_indirizzo_tipo === 'altro' && (
                  formData.invio_indirizzo_altro
                    ? `${formData.invio_indirizzo_altro}, ${formData.invio_cap_altro || ''} ${formData.invio_citta_altro || ''} (${formData.invio_provincia_altro || ''})`
                    : 'Altro indirizzo'
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Consensi */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-shield-check" />
          <span>Consensi</span>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Privacy</span>
            <span className={styles.summaryValue}>
              {formData.consenso_privacy ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-success-600)' }}>
                  <i className="bi bi-check-circle-fill" /> Accettato
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-danger-600)' }}>
                  <i className="bi bi-x-circle-fill" /> Non accettato
                </span>
              )}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Marketing</span>
            <span className={styles.summaryValue}>
              {formData.consenso_marketing ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-success-600)' }}>
                  <i className="bi bi-check-circle-fill" /> Accettato
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-danger-600)' }}>
                  <i className="bi bi-x-circle-fill" /> Non accettato
                </span>
              )}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Comunicazioni</span>
            <span className={styles.summaryValue}>
              {formData.consenso_comunicazioni ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-success-600)' }}>
                  <i className="bi bi-check-circle-fill" /> Accettato
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--color-danger-600)' }}>
                  <i className="bi bi-x-circle-fill" /> Non accettato
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Documento d'Identità - solo se richiesto */}
      {documentRequired && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-person-vcard" />
            <span>Documento d'Identità</span>
            <button 
              type="button"
              className={styles.editButton}
              onClick={() => setStep('documento')}
              disabled={isSubmitting}
            >
              <i className="bi bi-pencil" />
              Modifica
            </button>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Tipo Documento</span>
              <span className={styles.summaryValue}>
                {formData.tipo_documento === 'carta_identita' && "Carta d'Identità"}
                {formData.tipo_documento === 'patente' && "Patente"}
                {formData.tipo_documento === 'passaporto' && "Passaporto"}
                {!formData.tipo_documento && '-'}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Numero</span>
              <span className={styles.summaryValue}>{formData.numero_documento || '-'}</span>
            </div>
            {formData.data_emissione_documento && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Data Emissione</span>
                <span className={styles.summaryValue}>{formData.data_emissione_documento}</span>
              </div>
            )}
            {formData.data_scadenza_documento && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Data Scadenza</span>
                <span className={styles.summaryValue}>{formData.data_scadenza_documento}</span>
              </div>
            )}
            {formData.ente_rilascio && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Ente Rilascio</span>
                <span className={styles.summaryValue}>{formData.ente_rilascio}</span>
              </div>
            )}
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Allegato</span>
              <span className={styles.summaryValue}>
                {hasRequiredDocs ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-success-600)' }}>
                    <i className="bi bi-check-circle-fill" />
                    Documento caricato ({identityDocsStatus?.count || 0} file)
                  </span>
                ) : attachmentRequired ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-danger-600)' }}>
                    <i className="bi bi-exclamation-triangle-fill" />
                    Non caricato
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-gray-500)' }}>
                    <i className="bi bi-dash-circle" />
                    Non richiesto per il cambio fornitore
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Warning for missing identity document */}
      {attachmentRequired && !hasRequiredDocs && (
        <div className={styles.warningBanner}>
          <i className="bi bi-exclamation-triangle-fill" />
          <div>
            <strong>Documento d'identità mancante</strong>
            <p>Carica fronte e retro del documento dalla sezione Documento per proseguire.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon="arrow-left"
              onClick={() => setStep('documento')}
              className={styles.warningButton}
            >
              Vai a Documento
            </Button>
          </div>
        </div>
      )}

      {/* Diario Consumi */}
      {formData.diario_consumi_attivo && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-house" />
            <span>Diario Consumi</span>
          </div>
          <div className={styles.summaryGrid}>
            {formData.superficie_abitazione != null && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Superficie Abitazione</span>
                <span className={styles.summaryValue}>{formData.superficie_abitazione} m²</span>
              </div>
            )}
            {formData.numero_abitanti != null && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Numero Abitanti</span>
                <span className={styles.summaryValue}>{formData.numero_abitanti}</span>
              </div>
            )}
            {formData.tipologia_abitazione && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Tipologia Abitazione</span>
                <span className={styles.summaryValue}>
                  {formData.tipologia_abitazione === 'appartamento' ? 'Appartamento' : 'Casa indipendente'}
                </span>
              </div>
            )}
            {formData.proprietario_abitazione != null && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Proprietario</span>
                <span className={styles.summaryValue}>{formData.proprietario_abitazione ? 'Sì' : 'No'}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Titolarità Immobile (solo voltura) */}
      {formData.titolarita_immobile && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-building" />
            <span>Titolarità Immobile</span>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Titolarità</span>
              <span className={styles.summaryValue}>
                {formData.titolarita_immobile === 'proprietario' && 'Proprietario'}
                {formData.titolarita_immobile === 'locatario' && 'Locatario'}
                {formData.titolarita_immobile === 'locatario_edilizia_pubblica' && 'Locatario Edilizia Pubblica'}
                {formData.titolarita_immobile === 'comodatario' && 'Comodatario'}
                {formData.titolarita_immobile === 'usufruttuario' && 'Usufruttuario'}
                {formData.titolarita_immobile === 'altro' && 'Altro'}
              </span>
            </div>
            {formData.catasto_sezione && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Sezione Catasto</span>
                <span className={styles.summaryValue}>{formData.catasto_sezione}</span>
              </div>
            )}
            {formData.catasto_foglio && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Foglio</span>
                <span className={styles.summaryValue}>{formData.catasto_foglio}</span>
              </div>
            )}
            {formData.catasto_particella && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Particella</span>
                <span className={styles.summaryValue}>{formData.catasto_particella}</span>
              </div>
            )}
            {formData.catasto_subalterno && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Subalterno</span>
                <span className={styles.summaryValue}>{formData.catasto_subalterno}</span>
              </div>
            )}
            {formData.titolarita_note && (
              <div className={clsx(styles.summaryItem, styles.fullWidth)}>
                <span className={styles.summaryLabel}>Note</span>
                <span className={styles.summaryValue}>{formData.titolarita_note}</span>
              </div>
            )}
            {formData.locazione_agenzia_entrate && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Agenzia Entrate</span>
                <span className={styles.summaryValue}>{formData.locazione_agenzia_entrate}</span>
              </div>
            )}
            {formData.locazione_data_registrazione && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Data Registrazione</span>
                <span className={styles.summaryValue}>{formData.locazione_data_registrazione}</span>
              </div>
            )}
            {formData.locazione_numero && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Numero Registrazione</span>
                <span className={styles.summaryValue}>{formData.locazione_numero}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorBanner}>
          <i className="bi bi-exclamation-triangle" />
          <span>{error}</span>
        </div>
      )}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={prevStep}
          disabled={isSubmitting}
        >
          Indietro
        </Button>
        
        <Button
          type="button"
          icon="pen"
          iconPosition="right"
          loading={isSubmitting}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Procedi alla Firma
        </Button>
      </div>
    </div>
  )
}

